import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { RootState } from "../../store/reducers";
import { IFinancialCategoryDataSummaryModel } from "../../types/models";
import { getShortMonthName } from "../../utils/common";
import { FinancialCategoryEnumModel } from "../../types/types";
import {
  ACTIVE_DOT_COLOR,
  splitAnalyticsRows,
  useAnalyticsYears,
} from "../../hooks/usePipeline";
import { getCompactLabel, getOptimalXAxisConfig } from "./xAxisConfig";

/** Averages of a percentage; every other category is a dollar total */
export const isPercentageCategory = (category: FinancialCategoryEnumModel) =>
  category === FinancialCategoryEnumModel.Commission ||
  category === FinancialCategoryEnumModel.NetIncomePercent;

/** Only these have usable figures for Active and Under Contract entries in the Pipeline folder */
export const hasPipelineDots = (category: FinancialCategoryEnumModel) =>
  category === FinancialCategoryEnumModel.Commission ||
  category === FinancialCategoryEnumModel.Gci;

interface FinancialLineChartWrapperProps {
  category: FinancialCategoryEnumModel;
  selectedYearIds?: number[];
  timePeriod: "quarter" | "month" | "year";
  /** Adds a "+ Under Contracts" dot per period and takes those out of the line */
  includeUnderContracts?: boolean;
}

interface ChartDataPoint {
  /** The line: closed deals (null for a year that only has dots) */
  value: number | null;
  /** "+ Under Contracts" dot: the line plus Under Contract deals */
  withUnderContract: number | null;
  /** "+ Active" dot: the line (+ Under Contracts) plus Active entries */
  withPipeline: number | null;
  displayPeriod: string;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

/** Fewer decimals when the percentages are far apart */
const getDecimalPlaces = (values: number[]) => {
  if (values.length === 0) return 2;
  const range = Math.max(...values) - Math.min(...values);
  if (range < 0.01) return 4;
  if (range < 0.1) return 3;
  return 2;
};

export const FinancialLineChartWrapper: React.FC<
  FinancialLineChartWrapperProps
> = ({ category, selectedYearIds, timePeriod, includeUnderContracts = false }) => {
  const theme = useTheme();
  const { financialCategoryState } = useSelector(
    (state: RootState) => state.analytics,
  );
  const { years: yearFolders, pipelineYearId } = useAnalyticsYears();
  const isPercentage = isPercentageCategory(category);

  const split = useMemo(
    () =>
      splitAnalyticsRows<IFinancialCategoryDataSummaryModel>(
        financialCategoryState.data,
        {
          years: yearFolders,
          selectedYearIds,
          pipelineYearId,
          includeUnderContracts,
        },
      ),
    [
      financialCategoryState.data,
      yearFolders,
      selectedYearIds,
      pipelineYearId,
      includeUnderContracts,
    ],
  );
  const isSingleYear = split.isSingleYear;

  const chartData: ChartDataPoint[] = useMemo(() => {
    const yearOf = (yearId: number) =>
      split.chartYears.find((y) => y.id === yearId)?.year ?? yearId;

    // Year folders on the axis, oldest first, each split into its quarters or months
    const periods = split.periodYearIds.flatMap((yearId) =>
      timePeriod === "year"
        ? [{ yearId, period: undefined as number | undefined }]
        : Array.from(
            { length: timePeriod === "month" ? 12 : 4 },
            (_, i) => ({ yearId, period: i + 1 as number | undefined }),
          ),
    );

    return periods.map(({ yearId, period }) => {
      // Sum the rows of one set (line, Under Contracts, Active) in this period
      const sumPeriod = (rows: IFinancialCategoryDataSummaryModel[]) => {
        const matching = rows.filter(
          (row) =>
            row.yearId === yearId &&
            (period === undefined ||
              (timePeriod === "month" ? row.month : row.quarter) === period),
        );
        return {
          count: matching.reduce((sum, row) => sum + (row.totalCount ?? 0), 0),
          amount: matching.reduce((sum, row) => sum + (row.totalAmount ?? 0), 0),
        };
      };
      const line = sumPeriod(split.actual);
      const underContract = sumPeriod(split.underContract);
      const pipeline = sumPeriod(split.pipeline);

      // A percentage is the average over the deals; a dollar figure is their total. A period
      // without deals plots 0, like the dollar figures (the dots only exist when they have deals)
      const plotted = (count: number, amount: number) =>
        isPercentage ? (count > 0 ? amount / count : 0) : amount;

      const ucCount = line.count + underContract.count;
      const ucAmount = line.amount + underContract.amount;

      const periodName =
        period === undefined
          ? ""
          : timePeriod === "month"
            ? getShortMonthName(period)
            : `Q${period}`;
      const displayPeriod =
        period === undefined
          ? `${yearOf(yearId)}`
          : isSingleYear
            ? periodName
            : `${yearOf(yearId)} ${periodName}`;

      return {
        value: split.isDotsOnlyYear(yearId)
          ? null
          : plotted(line.count, line.amount),
        withUnderContract:
          underContract.count > 0 ? plotted(ucCount, ucAmount) : null,
        withPipeline:
          pipeline.count > 0
            ? plotted(ucCount + pipeline.count, ucAmount + pipeline.amount)
            : null,
        displayPeriod,
      };
    });
  }, [split, timePeriod, isSingleYear, isPercentage]);

  const xAxisConfig = getOptimalXAxisConfig(chartData.length, timePeriod);

  const decimalPlaces = getDecimalPlaces(
    chartData.flatMap((d) =>
      [d.value, d.withUnderContract, d.withPipeline].filter(
        (v): v is number => v !== null,
      ),
    ),
  );
  const formatValue = (value: number) =>
    isPercentage ? `${value.toFixed(decimalPlaces)}%` : formatCurrency(value);

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const point: ChartDataPoint = payload[0].payload;
    return (
      <div
        style={{
          backgroundColor: "white",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: "4px",
        }}
      >
        <p style={{ margin: 0, fontWeight: "bold" }}>{point.displayPeriod}</p>
        {payload
          .filter((entry: any) => entry.value !== null && entry.value !== undefined)
          .map((entry: any) => (
            <p key={entry.dataKey} style={{ margin: "4px 0 0 0", color: entry.color }}>
              {entry.name}: {formatValue(entry.value)}
            </p>
          ))}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart
        data={chartData}
        margin={{
          top: 10,
          right: xAxisConfig.angle !== 0 ? 50 : 30,
          left: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        {/* A crowded axis (e.g. All Years by month) only labels every few periods */}
        <XAxis
          dataKey="displayPeriod"
          type="category"
          tick={{ fontSize: xAxisConfig.fontSize }}
          interval={xAxisConfig.interval}
          angle={xAxisConfig.angle}
          textAnchor={xAxisConfig.textAnchor}
          height={xAxisConfig.height}
          tickFormatter={(value) =>
            getCompactLabel(value, timePeriod, chartData.length)
          }
        />
        <YAxis
          allowDecimals={true}
          domain={[0, "auto"]}
          tickCount={isPercentage ? 6 : undefined}
          tickFormatter={(value) => {
            if (isPercentage) return `${Number(value).toFixed(decimalPlaces)}%`;
            // Large numbers abbreviated
            const absValue = Math.abs(value);
            if (absValue >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
            if (absValue >= 1000) return `$${(value / 1000).toFixed(1)}K`;
            return formatCurrency(value);
          }}
        />
        <Tooltip content={<CustomTooltip />} />
        {/* Space below the legend so it clears the top Y-axis label */}
        <Legend
          verticalAlign="top"
          align="left"
          wrapperStyle={{ paddingBottom: 20 }}
        />
        <Line
          type="monotone"
          dataKey="value"
          stroke={theme.palette.primary.main}
          strokeWidth={2}
          activeDot={{ r: 8 }}
          name="Closed"
          connectNulls={true}
          legendType={
            split.includeUnderContracts || split.includePipeline
              ? "line"
              : "none"
          }
        />
        {split.includeUnderContracts && (
          <Line
            dataKey="withUnderContract"
            stroke={theme.palette.warning.main}
            strokeWidth={0}
            dot={{ fill: theme.palette.warning.main, stroke: "none", r: 6 }}
            activeDot={{ fill: theme.palette.warning.main, r: 8 }}
            name="+ Under Contracts"
            connectNulls={false}
            legendType="circle"
          />
        )}
        {split.includePipeline && (
          <Line
            dataKey="withPipeline"
            stroke={ACTIVE_DOT_COLOR}
            strokeWidth={0}
            dot={{ fill: ACTIVE_DOT_COLOR, stroke: "none", r: 6 }}
            activeDot={{ fill: ACTIVE_DOT_COLOR, r: 8 }}
            name="+ Active"
            connectNulls={false}
            legendType="circle"
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};
